import type { SectionEightSimplifiedFields, SectionNineSimplifiedFields, SectionSevenSimplifiedFields, SectionSixSimplifiedFields } from "./domain";
import { normaliseEditableReportText } from "./report-text";

type StringFields<TFields>={ [K in keyof TFields]:string };

export interface SimplifiedFieldDefinition<TFields extends StringFields<TFields>>{
  key:Extract<keyof TFields,string>;
  label:string;
  hint:string;
}

export const sectionSixSimplifiedFieldDefinitions:Array<SimplifiedFieldDefinition<SectionSixSimplifiedFields>>=[
  {key:"confirmedCommercialPriorities",label:"Commercial priorities",hint:"Keep confirmed priorities separate from priorities that still need confirmation. Do not promote provisional information automatically."},
  {key:"mainVisibilityGaps",label:"Main visibility gaps",hint:"Keep assessable absence separate from unsuitable, unavailable and untested evidence."},
  {key:"commercialImportance",label:"What matters most commercially",hint:"Explain likely effects on customer matching, trust, conversion or enquiries without claiming lost clients or revenue."},
  {key:"priorityDirection",label:"Priority direction",hint:"Describe future work. Do not imply that implementation has already happened."},
];

export const sectionSevenSimplifiedFieldDefinitions:Array<SimplifiedFieldDefinition<SectionSevenSimplifiedFields>>=[
  {key:"confirmedCommercialCompetitors",label:"Confirmed commercial competitors",hint:"Include only entities Steve has reviewed and approved as genuine service competitors."},
  {key:"frequentCompetitors",label:"Competitors appearing most frequently",hint:"Show no more than six approved competitors and state the assessable-result denominator."},
  {key:"howTheyAppeared",label:"How they appeared",hint:"Keep recommendation, shortlist inclusion, mention and citation separate."},
  {key:"commercialMeaning",label:"What this means",hint:"Explain the reviewed commercial consequence without claiming actual lost clients, enquiries or revenue."},
];

export const sectionEightSimplifiedFieldDefinitions:Array<SimplifiedFieldDefinition<SectionEightSimplifiedFields>>=[
  {key:"currentAuthoritySignals",label:"Current authority signals",hint:"Summarise confirmed owned and third-party signals without overstating incomplete evidence."},
  {key:"mainAuthorityGaps",label:"Main authority gaps",hint:"Keep confirmed absence separate from weak, unknown, unavailable, untested or incomplete evidence."},
  {key:"authorityOpportunities",label:"Opportunities to strengthen authority",hint:"Describe supported future work only. Do not imply that implementation has happened."},
  {key:"commercialImportance",label:"Why this matters commercially",hint:"Connect corroboration to trust, customer matching and conversion without promising outcomes."},
];

export const sectionNineSimplifiedFieldDefinitions:Array<SimplifiedFieldDefinition<SectionNineSimplifiedFields>>=[
  {key:"positioningMeaning",label:"What business positioning means",hint:"Use a plain explanation of positioning. Do not add evidence-status labels or present a finished positioning statement."},
  {key:"positioningBrief",label:"What the final positioning statement must include",hint:"Keep this as a structured brief. Label client-specific points Confirmed, Provisional or Requires client confirmation."},
  {key:"applicationLocations",label:"Where the agreed positioning should be used",hint:"Separate confirmed existing channels from channels requiring review or confirmation. Do not imply changes have been implemented."},
  {key:"nextSteps",label:"What needs to happen next",hint:"Prescribe the future positioning work without completing it or creating Section 10 actions."},
];

function tidyLine(value:string){
  return normaliseEditableReportText(value.replace(/[ \t]+/g," ").trim());
}

export function localEditSuggestion(value:string){
  const paragraphs=value.replace(/\r\n/g,"\n").split(/\n{2,}/).map(paragraph=>{
    const source=paragraph.split("\n").map(tidyLine).filter(Boolean);
    const unique=source.filter((line,index)=>source.indexOf(line)===index);
    if(unique.length>=3&&unique.every(line=>line.length<180)){
      return unique.map(line=>`- ${line.replace(/^[-*]\s+/,"")}`).join("\n");
    }
    return unique.join("\n");
  }).filter(Boolean);
  return paragraphs.join("\n\n");
}

export function faithfulClientCopy<TFields extends StringFields<TFields>>(fields:TFields):TFields{
  return Object.fromEntries(Object.entries(fields).map(([key,value])=>[
    key,
    normaliseEditableReportText(String(value).replace(/[ \t]+\n/g,"\n").replace(/\n{3,}/g,"\n\n").trim()),
  ])) as TFields;
}

export function missingSimplifiedFields<TFields extends StringFields<TFields>>(fields:TFields,definitions:Array<SimplifiedFieldDefinition<TFields>>){
  return definitions.filter(field=>fields[field.key].trim().length<20).map(field=>field.label);
}

export function missingSectionSixFields(fields:SectionSixSimplifiedFields){
  return missingSimplifiedFields(fields,sectionSixSimplifiedFieldDefinitions);
}
